import React from 'react';
import { AsyncPaginate } from 'react-select-async-paginate';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

const SearchableInventorySelect = ({
  value,
  onChange,
  currentData = [],
  placeholder = 'Search inventory items...',
  isDisabled = false,
  filteredBusinessInventoryList = [],
}) => {
  const { t } = useTranslation();

  // Load options function for AsyncPaginate
  const loadOptions = async (searchTerm, { page }) => {
    const pageSize = 50;
    const currentPage = page || 1;

    // Filter items based on search term
    const filteredItems = searchTerm
      ? filteredBusinessInventoryList?.filter((item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : filteredBusinessInventoryList;

    // Calculate pagination
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const slicedItems = filteredItems.slice(startIndex, endIndex);

    // Convert to react-select format and mark disabled items
    const options = slicedItems.map((item) => {
      const isDisabled = !!currentData?.find(
        (data) => data?.uuid === item?.uuid
      );

      return {
        value: item.uuid,
        label: item.name,
        isDisabled,
        data: item,
      };
    });

    const hasMore = endIndex < filteredItems.length;

    return {
      options,
      hasMore,
      additional: {
        page: currentPage + 1,
      },
    };
  };

  // Custom option component to show disabled state
  const formatOptionLabel = (option) => (
    <Box>
      <Typography
        variant="body2"
        sx={{
          color: option.isDisabled ? 'text.disabled' : 'text.primary',
          fontWeight: option.isDisabled ? 'normal' : 'medium',
        }}
      >
        {option.label}
      </Typography>
    </Box>
  );

  // Custom styles for react-select
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: 6,
      borderColor: state.isFocused ? '#64b5f6' : '#e0e0e0',
      boxShadow: state.isFocused ? '0 0 0 1px #64b5f6' : 'none',
      minHeight: '44px',
      '&:hover': {
        borderColor: '#64b5f6',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#64b5f6'
        : state.isFocused
        ? '#e3f2fd'
        : 'white',
      color: state.isDisabled
        ? '#bdbdbd'
        : state.isSelected
        ? 'white'
        : 'black',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      opacity: state.isDisabled ? 0.6 : 1,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9e9e9e',
    }),
    loadingMessage: (provided) => ({
      ...provided,
      color: '#64b5f6',
    }),
    noOptionsMessage: (provided) => ({
      ...provided,
      color: '#757575',
    }),
  };

  return (
    <Box sx={{ minHeight: '64px' }}>
      <AsyncPaginate
        value={value}
        onChange={onChange}
        loadOptions={loadOptions}
        placeholder={t(placeholder)}
        formatOptionLabel={formatOptionLabel}
        styles={customStyles}
        isDisabled={isDisabled}
        isOptionDisabled={(option) => option.isDisabled}
        isClearable
        isSearchable
        cacheOptions
        defaultOptions
        additional={{ page: 1 }}
        loadingMessage={() => t('Loading more items...')}
        noOptionsMessage={() => t('No items found')}
        menuPosition="fixed"
        menuPlacement="auto"
        maxMenuHeight={300}
      />
    </Box>
  );
};

export default SearchableInventorySelect;
